import { AutoComplete } from "primereact/autocomplete";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled from "styled-components";

import { useChangeClaimProviderMutation } from "../../store/queries/claims";
import { useGetProvidersByAccountQuery } from "../../store/queries/providers";
import { IClaim } from "../../types/claim";

interface IProviderSelectorProps {
  claim: IClaim;
  onClaimUpdated: (claim: IClaim) => void;
  onNavigateToClaim?: (claimId: string) => void;
}

interface IProviderOption {
  name: string;
  id: string;
}

const SelectorContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ProviderName = styled.span`
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

const EditButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #6c757d;
  padding: 0.15rem 0.3rem;
  border-radius: 4px;
  font-size: 0.85rem;
  display: inline-flex;
  align-items: center;

  &:hover {
    background-color: #e9ecef;
    color: #495057;
  }
`;

/**
 * CLE-2500: show the mapped provider on the claim modal and allow changing it,
 * mirroring the matched-payer control (PayerSelector). Changing the provider
 * re-prices the claim (or episode) under the new provider's fee schedule.
 *
 * The picker is scoped to providers in the claim's own account — the backend
 * rejects cross-account remaps, so offering them would only produce errors.
 * Unlike payer there is no "No Match" option: a claim must price against some
 * provider.
 */
export function ProviderSelector({
  claim,
  onClaimUpdated,
  onNavigateToClaim,
}: IProviderSelectorProps) {
  const toastRef = useRef<Toast>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [searchValue, setSearchValue] = useState<string | IProviderOption>("");
  const [filteredProviders, setFilteredProviders] = useState<IProviderOption[]>(
    []
  );
  const isSelectingRef = useRef(false);

  // Scope the picker to the claim's own account (the backend rejects cross-account
  // remaps). Every claim row carries a NOT NULL accountId, so skip is a safety net.
  const { data: providersData } = useGetProvidersByAccountQuery(
    { accountId: claim.accountId },
    { skip: !claim.accountId }
  );
  const [changeClaimProvider, { isLoading: isChanging }] =
    useChangeClaimProviderMutation();

  const providers: IProviderOption[] = useMemo(
    () => (providersData?.data || []).map((p) => ({ name: p.name, id: p.id })),
    [providersData]
  );

  const currentProviderName =
    providers.find((p) => p.id === claim.providerId)?.name || "-";

  const onProviderSearch = (e: any) => {
    const query = e.query.toLowerCase();
    setFilteredProviders(
      providers.filter((provider) =>
        provider.name.toLowerCase().includes(query)
      )
    );
  };

  const submitProviderChange = async (selectedProvider: IProviderOption) => {
    setIsEditing(false);
    setSearchValue("");

    if (selectedProvider.id === claim.providerId) {
      return;
    }

    try {
      const result = await changeClaimProvider({
        claimId: claim.id,
        providerId: selectedProvider.id,
      }).unwrap();

      toastRef.current?.show({
        severity: "success",
        summary: "Provider Updated",
        detail: `Mapped provider changed to ${selectedProvider.name}. Claim has been reprocessed.`,
        life: 5000,
      });

      if (result.data) {
        onClaimUpdated(result.data);
        if (onNavigateToClaim && result.data.id !== claim.id) {
          onNavigateToClaim(result.data.id);
        }
      }
    } catch (error: any) {
      toastRef.current?.show({
        severity: "error",
        summary: "Failed to Change Provider",
        detail:
          error?.data?.error ||
          error?.data?.messages?.[0] ||
          "An error occurred while changing the provider.",
        life: 8000,
      });
    }
  };

  // Close editor when clicking outside the component (including the dropdown overlay)
  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (isSelectingRef.current) {
      isSelectingRef.current = false;
      return;
    }
    const target = e.target as HTMLElement;
    const isInsideContainer = containerRef.current?.contains(target);
    const isInsideOverlay = target.closest(".p-autocomplete-panel");
    if (!isInsideContainer && !isInsideOverlay) {
      setIsEditing(false);
      setSearchValue("");
    }
  }, []);

  useEffect(() => {
    if (!isEditing) {
      return undefined;
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isEditing, handleClickOutside]);

  if (isChanging) {
    return (
      <>
        <Toast ref={toastRef} />
        <SelectorContainer>
          <div style={{ width: "18px", height: "18px", flexShrink: 0 }}>
            <ProgressSpinner
              style={{ width: "18px", height: "18px" }}
              strokeWidth="4"
            />
          </div>
          <span style={{ color: "#6c757d", fontSize: "0.9rem" }}>
            Reprocessing with new provider...
          </span>
        </SelectorContainer>
      </>
    );
  }

  if (isEditing) {
    return (
      <>
        <Toast ref={toastRef} />
        <SelectorContainer ref={containerRef}>
          <AutoComplete
            value={searchValue}
            suggestions={filteredProviders}
            field="name"
            completeMethod={onProviderSearch}
            onChange={(e) => setSearchValue(e.value)}
            onSelect={(e) => {
              isSelectingRef.current = true;
              submitProviderChange(e.value as IProviderOption);
            }}
            placeholder="Search providers..."
            dropdown
            autoFocus
            style={{ width: "100%" }}
            inputStyle={{ width: "100%" }}
          />
        </SelectorContainer>
      </>
    );
  }

  return (
    <>
      <Toast ref={toastRef} />
      <SelectorContainer>
        <ProviderName onClick={() => setIsEditing(true)}>
          {currentProviderName}
        </ProviderName>
        <EditButton
          onClick={() => setIsEditing(true)}
          title="Change mapped provider"
        >
          <i className="pi pi-pencil" />
        </EditButton>
      </SelectorContainer>
    </>
  );
}
